import { useCallback, useMemo, useState } from "react";

import {
  faExclamationCircle,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import type { Layout } from "react-grid-layout";
import { GridLayout, verticalCompactor } from "react-grid-layout";
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import useMeasure from "react-use-measure";

import { AddBlockPreview } from "src/cases/templates/caseBlocks/AddBlockPreview";
import { BackgroundGrid } from "src/cases/templates/caseBlocks/BackgroundGrid";
import { BlockCard } from "src/cases/templates/caseBlocks/BlockCard";
import { useCaseBlocksConfigActions } from "src/cases/templates/caseBlocks/CaseBlocksConfigContext";
import {
  GRID_COLUMNS,
  GRID_GAP,
  RGL_CONTAINER_PADDING,
  RGL_MARGIN,
  ROW_HEIGHT,
} from "src/cases/templates/caseBlocks/gridConstants";
import {
  calculateMinRows,
  calculateNextBlockPosition,
} from "src/cases/templates/caseBlocks/gridPositionUtils";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { Button } from "src/design-system/Button";
import "src/cases/templates/caseBlocks/rglStyles.css";

import {
  applyLayoutToBlocks,
  blocksToLayout,
} from "src/cases/templates/caseBlocks/rglTypes";
import {
  ADD_BLOCK_PREVIEW_ID,
  AddBlockPreviewLayoutItem,
  useGridHover,
} from "src/cases/templates/caseBlocks/useGridHover";
import { getStepById } from "src/cases/templates/formSteps";
import {
  CaseBlockConfigFormValue,
  CaseTemplateFormValues,
} from "src/cases/templates/formUtils/types";
import { generateInternalId } from "src/cases/templates/internalIdUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import {
  CaseBlockPositioning,
  LinkedBlockConfigAttributeKind,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

const createNewBlockConfig = (
  positioning: CaseBlockPositioning,
): CaseBlockConfigFormValue => {
  return {
    key: "",
    _internalId: generateInternalId(),
    title: "",
    icon: null,
    case_block_positioning: positioning,
    config: {
      kind: "table",
      source: { kind: LinkedBlockConfigAttributeKind.ADDITIONAL_INPUTS },
    },
  };
};

export const CaseBlocksConfigurationStep: React.FC = () => {
  const step = getStepById("configure-case-blocks");
  const form = useFormContext<CaseTemplateFormValues>();
  const { getValues, setValue } = form;
  const [containerRef, { width: containerWidth }] = useMeasure();
  const { selectBlock } = useCaseBlocksConfigActions();
  const hasPermission = useCheckFieldPermission();
  const canAddBlock = hasPermission("add", "attributes.case_blocks_configs");

  const { fields, remove, append } = useFieldArray({
    control: form.control,
    name: "attributes.case_blocks_configs",
  });

  const {
    addBlockPreviewItem,
    handleMouseMove,
    handleMouseLeave,
    clearAddBlockPreviewItem,
  } = useGridHover({
    blocks: fields,
    containerWidth,
  });

  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      const currentBlocks = getValues("attributes.case_blocks_configs");
      const index = currentBlocks.findIndex((f) => f._internalId === blockId);
      if (index !== -1) {
        const deletedBlock = currentBlocks[index];
        const blocksSnapshot = [...currentBlocks];

        remove(index);

        const toastId = toastActions.default({
          title: "Block deleted",
          description: `${deletedBlock.title || "Untitled block"} removed from the case layout.`,
          actionText: "Undo",
          duration: 7000,
          onActionClick: () => {
            setValue("attributes.case_blocks_configs", blocksSnapshot, {
              shouldDirty: true,
            });
            toastActions.dismiss(toastId);
          },
        });
      }
    },
    [getValues, setValue, remove],
  );

  const hasBlocks = useHasBlocks({ control: form.control });
  const minRows = useMinRows({ control: form.control });

  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      const currentBlocks = getValues("attributes.case_blocks_configs");
      const filteredLayout = newLayout.filter(
        (item) => item.i !== ADD_BLOCK_PREVIEW_ID,
      );
      const updatedBlocks = applyLayoutToBlocks(currentBlocks, filteredLayout);

      if (updatedBlocks) {
        setValue("attributes.case_blocks_configs", updatedBlocks, {
          shouldDirty: true,
        });
      }
    },
    [getValues, setValue],
  );

  const handleAddBlock = useCallback(() => {
    const currentBlocks = getValues("attributes.case_blocks_configs");
    const newBlock = createNewBlockConfig(
      calculateNextBlockPosition(currentBlocks),
    );
    append(newBlock);
    selectBlock(newBlock._internalId);
  }, [append, getValues, selectBlock]);

  const handleAddBlockAtPosition = useCallback(
    (item: AddBlockPreviewLayoutItem) => {
      const newBlock = createNewBlockConfig({
        column: item.x + 1,
        row: item.y + 1,
        width: item.w,
        height: item.h,
      });
      append(newBlock);
      clearAddBlockPreviewItem();
      selectBlock(newBlock._internalId);
    },
    [append, selectBlock, clearAddBlockPreviewItem],
  );

  return (
    <FormStepWrapper
      actions={
        canAddBlock && (
          <Button
            dataLoc="add-case-block-button"
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddBlock}
          >
            Add block
          </Button>
        )
      }
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      <ErrorBoundary
        fallbackErrorContent={
          <div className="flex h-full min-h-[200px] items-center justify-center">
            <EmptyState
              description="We're having trouble displaying the case blocks grid. Please refresh the page or contact support if the problem persists."
              headline="Case Blocks Error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        }
      >
        <div
          ref={containerRef}
          className="relative isolate"
          data-loc="case-blocks-config-grid"
          style={{
            minHeight: `${minRows * ROW_HEIGHT + (minRows - 1) * GRID_GAP}px`,
          }}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          <BackgroundGrid rows={minRows} />
          <CaseBlocksGrid
            addBlockPreviewItem={addBlockPreviewItem}
            containerWidth={containerWidth}
            fields={fields}
            hasBlocks={hasBlocks}
            selectBlock={selectBlock}
            onAddBlockAtPosition={handleAddBlockAtPosition}
            onDeleteBlock={handleDeleteBlock}
            onLayoutChange={handleLayoutChange}
          />
        </div>
      </ErrorBoundary>
    </FormStepWrapper>
  );
};

type CaseBlocksGridProps = {
  fields: CaseBlockConfigFormValue[];
  containerWidth: number;
  hasBlocks: boolean;
  addBlockPreviewItem: AddBlockPreviewLayoutItem | null;
  selectBlock: (id: string | null) => void;
  onLayoutChange: (newLayout: Layout) => void;
  onDeleteBlock: (blockId: string) => void;
  onAddBlockAtPosition: (item: AddBlockPreviewLayoutItem) => void;
};

const CaseBlocksGrid: React.FC<CaseBlocksGridProps> = ({
  fields,
  containerWidth,
  hasBlocks,
  addBlockPreviewItem,
  selectBlock,
  onLayoutChange,
  onDeleteBlock,
  onAddBlockAtPosition,
}) => {
  const hasPermission = useCheckFieldPermission();
  const canAddBlock = hasPermission("add", "attributes.case_blocks_configs");
  const canSortBlocks = hasPermission("sort", "attributes.case_blocks_configs");
  const [isInteracting, setIsInteracting] = useState(false);

  const layout = useMemo(() => blocksToLayout(fields), [fields]);

  const displayLayout = useMemo(() => {
    if (!canAddBlock || !addBlockPreviewItem || isInteracting) return layout;
    return [...layout, addBlockPreviewItem];
  }, [layout, addBlockPreviewItem, isInteracting, canAddBlock]);

  const handleDragStart = useCallback(() => {
    setIsInteracting(true);
    selectBlock(null);
  }, [selectBlock]);

  const handleDragStop = useCallback(() => {
    setIsInteracting(false);
  }, []);

  const handleResizeStart = useCallback(() => {
    setIsInteracting(true);
  }, []);

  const handleResizeStop = useCallback(() => {
    setIsInteracting(false);
  }, []);

  const showAddBlockPreview =
    canAddBlock && addBlockPreviewItem && !isInteracting;
  const showGridLayout =
    (hasBlocks || showAddBlockPreview) && containerWidth > 0;

  if (!showGridLayout) {
    return null;
  }

  return (
    <GridLayout
      compactor={verticalCompactor}
      dragConfig={{
        enabled: canSortBlocks,
        handle: ".drag-handle",
      }}
      gridConfig={{
        cols: GRID_COLUMNS,
        rowHeight: ROW_HEIGHT,
        margin: RGL_MARGIN,
        containerPadding: RGL_CONTAINER_PADDING,
      }}
      layout={displayLayout}
      resizeConfig={{
        enabled: canSortBlocks,
        handles: ["se"],
      }}
      width={containerWidth}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onLayoutChange={onLayoutChange}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
    >
      {fields.map((block) => (
        <div
          key={block._internalId}
          className="group h-full"
          data-block-id={block._internalId}
        >
          <BlockCard
            blockInternalId={block._internalId}
            onDelete={() => onDeleteBlock(block._internalId)}
          />
        </div>
      ))}
      {showAddBlockPreview && (
        <div key={ADD_BLOCK_PREVIEW_ID}>
          <AddBlockPreview
            onClick={() => onAddBlockAtPosition(addBlockPreviewItem)}
          />
        </div>
      )}
    </GridLayout>
  );
};

const useHasBlocks = ({
  control,
}: {
  control: Control<CaseTemplateFormValues>;
}) => {
  return useWatch({
    control,
    name: "attributes.case_blocks_configs",
    compute: (value) => Array.isArray(value) && value.length > 0,
  });
};

const useMinRows = ({
  control,
}: {
  control: Control<CaseTemplateFormValues>;
}) => {
  return useWatch({
    control,
    name: "attributes.case_blocks_configs",
    compute: (value) => calculateMinRows(value ?? []),
  });
};
